/**
 * I18n React Hooks
 * useTranslation and useLanguage hooks for React components
 * Handles language state and translation function
 * @module @infra/i18n
 */

import { useCallback, useEffect, useState } from 'react';
import type { Language, TranslationKey, TranslationParams } from './i18n.types';
import { getI18nService } from './i18n';
import { persistLanguageChoice } from './i18n.persistence';
import { I18N_LANGUAGE_KEY } from './i18n.constants';

const i18nService = getI18nService();

/**
 * useTranslation hook
 * Returns synchronous translation function and current language
 * Usage: const { t, language, changeLanguage } = useTranslation();
 */
export function useTranslation() {
  const [language, setLanguage] = useState<Language>(i18nService.getLanguage());
  const [namespaceVersion, setNamespaceVersion] = useState(0);

  useEffect(() => {
    const handleLanguageChange = () => {
      setLanguage(i18nService.getLanguage());
    };

    const handleNamespaceLoaded = () => {
      setNamespaceVersion((version) => version + 1);
    };

    window.addEventListener('i18n:languageChange', handleLanguageChange);
    window.addEventListener('i18n:namespaceLoaded', handleNamespaceLoaded);

    return () => {
      window.removeEventListener('i18n:languageChange', handleLanguageChange);
      window.removeEventListener('i18n:namespaceLoaded', handleNamespaceLoaded);
    };
  }, []);

  // Return synchronous translation function.
  // `language` ensures downstream useMemo/useEffect recompute when the user
  // switches locale; `namespaceVersion` covers the async-namespace-loaded path.
  const t = useCallback(
    (key: TranslationKey, params?: TranslationParams): string => {
      return i18nService.t(key, params);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- language/namespaceVersion aren't read in the body; they force `t` to a new identity so callers re-render on locale/namespace change.
    [language, namespaceVersion]
  );

  /**
   * @deprecated Use `useLanguageSwitch()` instead. Two things this does not do: it
   * never writes the choice to the account, so the next login reverts it; and it does
   * not reload, leaving react-query data fetched with the old Accept-Language stale →
   * mixed-language UI. 0 call sites; kept only for the return-object shape.
   */
  const changeLanguage = useCallback(async (newLanguage: Language) => {
    await i18nService.setLanguage(newLanguage);
    setLanguage(newLanguage);
  }, []);

  return { t, language, changeLanguage };
}

/** Whether the language just set will still be there after a reload. */
function languageChoiceSurvivesReload(code: string): boolean {
  try {
    return localStorage.getItem(I18N_LANGUAGE_KEY) === code;
  } catch {
    return false;
  }
}

/** Guards a double-click: the first switch reloads the page; further clicks no-op until then. */
let languageSwitchInFlight = false;

/** How long to wait for a requested reload before assuming it was cancelled. */
const LANGUAGE_SWITCH_RELOAD_GRACE_MS = 3_000;

/**
 * The three ways a switch can end. A caller showing a spinner has to clear it on
 * anything but `reloading`, since only that one takes the document away.
 */
export type LanguageSwitchOutcome =
  /** The page is going away and will come back in the new language. */
  | 'reloading'
  /** Language changed in place; language-scoped data stays as it was fetched. */
  | 'applied-in-place'
  /** Another switch was already running, so this one did nothing. */
  | 'ignored';

/**
 * useLanguageSwitch hook
 * Switch language then hard-reload so every request re-fires with the new
 * Accept-Language. react-query cache isn't keyed by language, so the reload is the
 * deliberate mechanism to refetch language-scoped data cleanly. Throws if the
 * locale fails to load (reload skipped → stay on current language); callers with a
 * loading UI should catch to reset it, and read the outcome for the paths that
 * resolve without reloading.
 */
export function useLanguageSwitch() {
  return useCallback(async (code: Language): Promise<LanguageSwitchOutcome> => {
    if (languageSwitchInFlight) return 'ignored'; // ignore rapid double-clicks
    languageSwitchInFlight = true;
    try {
      await getI18nService().setLanguage(code);
      // Persist the choice to the backend (DB + FusionAuth) BEFORE reloading —
      // a reload would cancel the in-flight request. Best-effort: a persistence
      // failure (or an anonymous user) must not block the UI language switch.
      try {
        await persistLanguageChoice(code);
      } catch {
        /* non-critical — the UI language still changes */
      }
      // A reload is only safe once the choice is somewhere the next load will read.
      // Storage can refuse the write — private windows, blocked site data — and
      // `setLanguage` treats that as non-fatal; reloading anyway would start the app
      // on the old language and, where something switches on every load, reload
      // forever. The in-memory switch and its event already happened, so staying put
      // costs only the react-query data still fetched in the old language.
      if (!languageChoiceSurvivesReload(code)) {
        languageSwitchInFlight = false;
        return 'applied-in-place';
      }
      window.location.reload();
      // `reload()` is a request, not a guarantee — a `beforeunload` handler can
      // cancel it and execution simply continues here. Without this the guard stays
      // latched and the switcher is dead until the next full page load. The timer
      // dies with the document when the reload does go through.
      setTimeout(() => {
        languageSwitchInFlight = false;
      }, LANGUAGE_SWITCH_RELOAD_GRACE_MS);
      return 'reloading';
    } catch (error) {
      // Locale failed to load → reload skipped. Clear the flag so the user can retry, and
      // rethrow so a caller's loading UI can reset (callers must `.catch()`).
      languageSwitchInFlight = false;
      throw error;
    }
  }, []);
}

/**
 * useLanguage hook
 * Returns current language, change function, and available languages
 * Usage: const { language, changeLanguage, availableLanguages } = useLanguage();
 */
export function useLanguage() {
  const [language, setLanguage] = useState<Language>(i18nService.getLanguage());
  const [, setNamespaceVersion] = useState(0);

  useEffect(() => {
    const handleLanguageChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ language: Language }>;
      setLanguage(customEvent.detail.language);
    };

    const handleNamespaceLoaded = () => {
      setNamespaceVersion((version) => version + 1);
    };

    // `availableLanguages` below is recomputed on render, and the entitlement answer
    // that narrows it lands after first paint — a picker that already rendered the
    // full list would otherwise keep showing it.
    const handleAllowedChange = () => {
      setNamespaceVersion((version) => version + 1);
    };

    window.addEventListener('i18n:languageChange', handleLanguageChange);
    window.addEventListener('i18n:namespaceLoaded', handleNamespaceLoaded);
    window.addEventListener('i18n:allowedLanguagesChange', handleAllowedChange);

    return () => {
      window.removeEventListener('i18n:languageChange', handleLanguageChange);
      window.removeEventListener('i18n:namespaceLoaded', handleNamespaceLoaded);
      window.removeEventListener('i18n:allowedLanguagesChange', handleAllowedChange);
    };
  }, []);

  /**
   * @deprecated Use `useLanguageSwitch()` instead. Writes nothing to the account and
   * does not reload, so the choice is lost at the next login and react-query data
   * stays stale (mixed-language UI). 0 call sites; kept for the return-object shape.
   */
  const changeLanguage = useCallback(async (newLanguage: Language) => {
    await i18nService.setLanguage(newLanguage);
    setLanguage(newLanguage);
  }, []);

  return {
    language,
    changeLanguage,
    availableLanguages: i18nService.getAvailableLanguages(),
  };
}

/**
 * useI18n hook
 * Returns full i18n service for advanced usage
 */
export function useI18n() {
  return i18nService;
}
